/**
 * EmojiGrid.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Scrollable, virtualised grid of emoji image buttons.
 * Purely presentational — receives items and fires onSelect.
 */

import { useCallback, useLayoutEffect, useState } from 'react';
import { Box, SimpleGrid, Text, UnstyledButton } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { encodeEmojiPlaceholder } from './placeholder';
import { emojiPacks } from './emojiSets';
import { useEmojiVirtualizer, CELL_SIZE, CELL_GAP } from './useEmojiVirtualizer';
import type { EmojiItem } from './useEmojiUsage';

// ── Image prefetch cache ─────────────────────────────────────────────────────

const imageCache = new Set<string>();

function prefetch(urls: string[]) {
  const run = () => {
    for (const url of urls) {
      if (imageCache.has(url)) continue;
      imageCache.add(url);
      const img = new Image();
      img.src = url;
    }
  };
  if ('requestIdleCallback' in window) {
    (window as Window & { requestIdleCallback: (cb: () => void) => number }).requestIdleCallback(run);
  } else {
    setTimeout(run, 0);
  }
}

// ── Resolve helper ───────────────────────────────────────────────────────────

const packMap = new Map(emojiPacks.map((p) => [p.id, p]));

function resolveUrl(item: EmojiItem): string | null {
  return packMap.get(item.packId)?.resolveUrl(item.codepoints) ?? null;
}

// ── Component ────────────────────────────────────────────────────────────────

type EmojiGridProps = {
  items: EmojiItem[];
  onSelect: (codepoints: string) => void;
};

export function EmojiGrid({ items, onSelect }: EmojiGridProps) {
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(240);

  // Measure the scroll container to drive virtualisation
  const { ref: containerRef, width: containerWidth } = useElementSize<HTMLDivElement>();

  const columns = containerWidth
    ? Math.max(5, Math.min(10, Math.floor(containerWidth / (CELL_SIZE + CELL_GAP))))
    : 7;

  const { topSpacer, bottomSpacer, visibleItems } = useEmojiVirtualizer({
    items,
    columns,
    scrollTop,
    viewportHeight,
  });

  // Track container height for virtualiser
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setViewportHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Prefetch nearby rows
  useLayoutEffect(() => {
    const urls: string[] = [];
    for (const item of visibleItems) {
      const url = resolveUrl(item);
      if (url) urls.push(url);
    }
    if (urls.length) prefetch(urls);
  }, [visibleItems]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  if (items.length === 0) {
    return (
      <Box py="xl" ta="center">
        <Text size="sm" c="dimmed">暂无表情</Text>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {/* Top virtual spacer */}
      {topSpacer > 0 && <Box style={{ height: topSpacer }} />}

      <SimpleGrid cols={columns} spacing={CELL_GAP} verticalSpacing={CELL_GAP}>
        {visibleItems.map((item) => {
          const url = resolveUrl(item);
          const placeholder = encodeEmojiPlaceholder(item.codepoints);
          return (
            <UnstyledButton
              key={`${item.packId}:${item.codepoints}`}
              title={placeholder}
              onClick={() => onSelect(item.codepoints)}
              disabled={!url}
              style={{
                width: CELL_SIZE,
                height: CELL_SIZE,
                borderRadius: 8,
                background: 'var(--mantine-color-default)',
                display: 'grid',
                placeItems: 'center',
                transition: 'background 0.12s',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--mantine-color-default-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--mantine-color-default)'; }}
            >
              {url
                ? <img src={url} alt="" width={22} height={22} decoding="async" loading="lazy" />
                : <span style={{ fontSize: 10, color: 'var(--mantine-color-dimmed)' }}>?</span>
              }
            </UnstyledButton>
          );
        })}
      </SimpleGrid>

      {/* Bottom virtual spacer */}
      {bottomSpacer > 0 && <Box style={{ height: bottomSpacer }} />}
    </Box>
  );
}
